import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ChatbotActiveFilters, ChatbotMessage } from '@/models/chatbot';
import { chatbotQueryKeys } from './chatbotQueryKeys';
import useChatbotHistoryApi from './useChatbotHistoryApi';
import { mapHistoryMessagesToStore } from '../ChatHistory/mapHistoryMessagesToStore';

interface UseOpenConversationParams {
  onConversationLoaded: (
    conversationId: string,
    messages: ChatbotMessage[],
    pinnedFilters: ChatbotActiveFilters | null,
  ) => void;
}

interface UseOpenConversationReturn {
  openConversation: (conversationId: string) => Promise<void>;
  retryOpen: () => void;
  clearOpenError: () => void;
  openingId: string | null;
  openError: boolean;
}

const useOpenConversation = ({ onConversationLoaded }: UseOpenConversationParams): UseOpenConversationReturn => {
  const { getConversation } = useChatbotHistoryApi();
  const queryClient = useQueryClient();
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [openError, setOpenError] = useState(false);
  const latestRequestRef = useRef<string | null>(null);
  const lastFailedIdRef = useRef<string | null>(null);

  const openConversation = useCallback(
    async (conversationId: string): Promise<void> => {
      latestRequestRef.current = conversationId;
      setOpenError(false);
      setOpeningId(conversationId);

      try {
        const detail = await queryClient.fetchQuery({
          queryKey: chatbotQueryKeys.conversation(conversationId),
          queryFn: () => getConversation(conversationId),
          retry: false,
          staleTime: 0,
        });

        if (latestRequestRef.current !== conversationId) {
          return;
        }

        lastFailedIdRef.current = null;
        onConversationLoaded(
          detail.conversation.id,
          mapHistoryMessagesToStore(detail.messages),
          detail.conversation.active_filters ?? null,
        );
        setOpeningId(null);
      } catch {
        if (latestRequestRef.current !== conversationId) {
          return;
        }

        lastFailedIdRef.current = conversationId;
        setOpenError(true);
        setOpeningId(null);
      }
    },
    [getConversation, onConversationLoaded, queryClient],
  );

  const retryOpen = useCallback((): void => {
    const failedId = lastFailedIdRef.current;
    if (failedId !== null) {
      void openConversation(failedId);
    }
  }, [openConversation]);

  const clearOpenError = useCallback((): void => {
    lastFailedIdRef.current = null;
    setOpenError(false);
  }, []);

  return { openConversation, retryOpen, clearOpenError, openingId, openError };
};

export default useOpenConversation;
